import React from 'react';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import Folder from '../Folder/Folder';
import File from '../File/File';
import MissingStatementsRow from '../MissingStatementsRow/MissingStatementsRow';
import styles from './FileTree.module.scss';
import { FileNode } from '../../../../DataRoom';
import { convertEpochToDate } from '../../../../../../utils/utils';
import { DATE_FORMATS, toMonthIndex } from '../../../../../../utils/dateUtils';
import type { FeatureVisibility } from '../../../../../../utils/visibilityConfigUtils';
import type { MissingStatementRange } from '../../../../types';
import { INGESTION_CHIP } from '../../../../constants';

interface FileTreeProps {
    data: FileNode[];
    index: number;
    masterFolderName?: string;
    fetchFiles: Function;
    setShowDataRoomModal: (value: { showModal: boolean; docId?: number; currentDocType?: string }) => void;
    selectedItems?: Set<string>;
    onItemSelect?: (itemId: string) => void;
    showCheckboxes?: boolean;
    isFolderFullySelected?: (folderId: string) => boolean;
    fileViewType?: string;
    dataRoomDownloadVisibility: FeatureVisibility;
    addToReportsVisible: boolean;
    onSendForSigning?: (docId: string, fileName: string) => void;
    signableExtensions?: string[];
    showSignedCopyVisibility?: FeatureVisibility;
    /** Open account folders that have a Failed file (Review Now deep link). */
    expandFailedAccounts?: boolean;
    /** This account folder's gap ranges, rendered among its files. */
    missingStatements?: MissingStatementRange[];
    // isRecurCompany: boolean;
}

// dateUtils doesn't extend customParseFormat globally; the strict 3-arg dayjs() below needs it.
dayjs.extend(customParseFormat);

// The date formats babler's fileMetaData dates arrive in (same as useFetchDataRoomFiles).
const FILE_METADATA_DATE_FORMATS = [DATE_FORMATS.DATE_FORMAT_DDMMYYYY, DATE_FORMATS.DATE_FORMAT_DD_MM_YYYY_SLASH, DATE_FORMATS.ISO];

// Each gap renders right after the last file that ends on or before the gap starts (an account's
// files are already sorted by end date). Ranges at the same position stack as separate rows.
const groupGapsByInsertionPosition = (data: FileNode[], missingStatements: MissingStatementRange[]) => {
    const positions = new Map<number, MissingStatementRange[]>();
    missingStatements.forEach((range) => {
        const gapFrom = dayjs(range.from, DATE_FORMATS.ISO, true);
        let position = data.length;
        data.forEach((item, itemIndex) => {
            const endDate = item.type === 'file' ? dayjs(item.metaData?.fileMetaData?.endDate, FILE_METADATA_DATE_FORMATS, true) : null;
            if (endDate?.isValid() && !endDate.isAfter(gapFrom)) position = itemIndex + 1;
        });
        positions.set(position, [...(positions.get(position) ?? []), range]);
    });
    return positions;
};

// Month indexes → ISO first/last day. Built from the 1st of the month so today's date (29th-31st)
// can't overflow into the next month.
const monthIndexToRange = (fromMonthIndex: number, throughMonthIndex: number): MissingStatementRange => {
    const monthStart = (index: number) => dayjs(new Date(Math.floor(index / 12), index % 12, 1)).startOf('month');
    return {
        from: monthStart(fromMonthIndex).format(DATE_FORMATS.ISO),
        to: monthStart(throughMonthIndex).endOf('month').format(DATE_FORMATS.ISO),
    };
};

// A bank account folder: below the doc-type root (parentIndex 0 = root), with at least one direct
// child file carrying an ingestion chip (only bank statements get one).
const isBankAccountFolder = (folder: FileNode, parentIndex: number): boolean => {
    if (parentIndex === 0) return false;
    return (folder.innerFiles ?? []).some((child) => child.type === 'file' && !!child.metaData?.fileMetaData?.ingestionChip);
};

// Missing months of an account folder: the uncovered months between the earliest and latest month
// its Data Extracted files cover. None if any file has no chip (aion couldn't be asked).
const computeMissingStatementRanges = (folder: FileNode, parentIndex: number): MissingStatementRange[] => {
    if (!isBankAccountFolder(folder, parentIndex)) return [];
    const bankStatementFiles = (folder.innerFiles ?? []).filter((child) => child.type === 'file');
    if (bankStatementFiles.some((file) => !file.metaData?.fileMetaData?.ingestionChip)) return [];

    const coveredMonths = new Set<number>();
    bankStatementFiles.forEach((file) => {
        if (file.metaData?.fileMetaData?.ingestionChip !== INGESTION_CHIP.DATA_EXTRACTED) return;
        const start = dayjs(file.metaData?.fileMetaData?.startDate, FILE_METADATA_DATE_FORMATS, true);
        const end = dayjs(file.metaData?.fileMetaData?.endDate, FILE_METADATA_DATE_FORMATS, true);
        if (!start.isValid() || !end.isValid()) return;
        for (let m = toMonthIndex(start); m <= toMonthIndex(end); m += 1) coveredMonths.add(m);
    });
    if (coveredMonths.size < 2) return [];

    const earliestMonth = Math.min(...coveredMonths);
    const latestMonth = Math.max(...coveredMonths);
    const ranges: MissingStatementRange[] = [];
    let gapStart: number | null = null;
    for (let m = earliestMonth + 1; m < latestMonth; m += 1) {
        if (!coveredMonths.has(m)) {
            if (gapStart === null) gapStart = m;
        } else if (gapStart !== null) {
            ranges.push(monthIndexToRange(gapStart, m - 1));
            gapStart = null;
        }
    }
    if (gapStart !== null) ranges.push(monthIndexToRange(gapStart, latestMonth - 1));
    return ranges;
};

// An account folder's files in end-date order; undated ones keep their order, after the dated.
const sortAccountFolderChildren = (folder: FileNode, parentIndex: number): FileNode[] => {
    const innerFiles = folder.innerFiles ?? [];
    if (!isBankAccountFolder(folder, parentIndex)) return innerFiles;

    const parsedEndDate = (item: FileNode) =>
        item.type === 'file' ? dayjs(item.metaData?.fileMetaData?.endDate, FILE_METADATA_DATE_FORMATS, true) : null;

    return [...innerFiles].sort((a, b) => {
        const aEnd = parsedEndDate(a);
        const bEnd = parsedEndDate(b);
        const aValid = !!aEnd?.isValid();
        const bValid = !!bEnd?.isValid();
        if (aEnd && bEnd && aValid && bValid) return aEnd.valueOf() - bEnd.valueOf();
        if (aValid !== bValid) return aValid ? -1 : 1;
        return 0;
    });
};

const FileTree: React.FC<FileTreeProps> = ({
    data,
    index,
    masterFolderName,
    fetchFiles,
    setShowDataRoomModal,
    selectedItems = new Set(),
    onItemSelect,
    showCheckboxes = false,
    isFolderFullySelected,
    fileViewType,
    dataRoomDownloadVisibility,
    addToReportsVisible,
    onSendForSigning,
    signableExtensions = [],
    showSignedCopyVisibility,
    expandFailedAccounts,
    missingStatements,
    // isRecurCompany,
}) => {
    const gapPositions = missingStatements?.length ? groupGapsByInsertionPosition(data, missingStatements) : null;
    const renderGapRow = (position: number) => {
        const ranges = gapPositions?.get(position);
        if (!ranges?.length) return null;
        return ranges.map((range) => (
            <MissingStatementsRow key={`missing-statements-${position}-${range.from}`} missingStatements={range} index={index + 1} />
        ));
    };

    return (
        <div className={styles.fileTree}>
            {data.map((item, itemIndex) => (
                <React.Fragment key={`${item.docId || item.name}-${fileViewType}-${index}-${itemIndex}`}>
                    {item.type === 'folder' ? (
                        <Folder
                            // isRecurCompany={isRecurCompany}
                            fetchFiles={fetchFiles}
                            index={index + 1}
                            name={item.name}
                            innerFiles={sortAccountFolderChildren(item, index)}
                            lastUpdated={item.updatedAt ? convertEpochToDate(item.updatedAt) : '-'}
                            parentFolderName={masterFolderName ?? ''}
                            setShowDataRoomModal={setShowDataRoomModal}
                            isNew={item.isNew}
                            selectedItems={selectedItems}
                            onItemSelect={onItemSelect}
                            showCheckboxes={showCheckboxes}
                            isFolderFullySelected={isFolderFullySelected}
                            currentFileViewType={fileViewType}
                            dataRoomDownloadVisibility={dataRoomDownloadVisibility}
                            addToReportsVisible={addToReportsVisible}
                            onSendForSigning={onSendForSigning}
                            signableExtensions={signableExtensions}
                            showSignedCopyVisibility={showSignedCopyVisibility}
                            expandFailedAccounts={expandFailedAccounts}
                            isBankAccountFolder={isBankAccountFolder(item, index)}
                            missingStatements={computeMissingStatementRanges(item, index)}
                        />
                    ) : (
                        <File
                            // isRecurCompany={isRecurCompany}
                            fetchFiles={fetchFiles}
                            masterFolderName={masterFolderName ?? ''}
                            docId={item.docId ?? null}
                            index={index + 1}
                            name={item.name}
                            lastUpdated={item.updatedAt ? convertEpochToDate(item.updatedAt) : '-'}
                            setShowDataRoomModal={setShowDataRoomModal}
                            isNew={item.isNew}
                            startDate={item?.metaData?.fileMetaData?.startDate || ''}
                            endDate={item?.metaData?.fileMetaData?.endDate || ''}
                            selectedItems={selectedItems}
                            onItemSelect={onItemSelect}
                            showCheckboxes={showCheckboxes}
                            dataRoomDownloadVisibility={dataRoomDownloadVisibility}
                            addToReportsVisible={addToReportsVisible}
                            onSendForSigning={onSendForSigning}
                            signableExtensions={signableExtensions}
                            signedDocId={item.signedDocId}
                            hasSignedCopy={item.hasSignedCopy}
                            showSignedCopyVisibility={showSignedCopyVisibility}
                            fileMetaData={item?.metaData?.fileMetaData}
                        />
                    )}
                    {renderGapRow(itemIndex + 1)}
                </React.Fragment>
            ))}
        </div>
    );
};

export default FileTree;
